import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { notificationSettingsAPI } from '../services/api';
import NavigationBar from './NavigationBar';
import EventNavigation from './EventNavigation';
import LoadingSpinner from './LoadingSpinner';
import './NotificationSettings.css';

const NotificationSettings = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updating, setUpdating] = useState({});
    
    // Get the last event from localStorage for sidebar context
    const eventNameId = localStorage.getItem('last_event');

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await notificationSettingsAPI.getSettings();
            if (response.data.success) {
                setSettings(response.data.settings);
            } else {
                setError(response.data.error || 'Failed to load notification settings');
            }
        } catch (err) {
            console.error('Error fetching notification settings:', err);
            setError('Failed to load notification settings');
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (notification, field) => {
        const notificationId = notification.notificationID;
        const updateKey = `${notificationId}-${field}`;
        
        // Prevent multiple simultaneous updates for the same toggle
        if (updating[updateKey]) return;
        
        setUpdating(prev => ({ ...prev, [updateKey]: true }));
        
        // Optimistically update the UI
        const newValue = !notification[field];
        setSettings(prev => prev.map(type => ({
            ...type,
            notificationsTypeList: type.notificationsTypeList.map(n => 
                n.notificationID === notificationId 
                    ? { ...n, [field]: newValue }
                    : n
            )
        })));
        
        try {
            // Only send the field being toggled
            const toggleData = {
                notificationID: notification.notificationID,
            };
            
            // Add only the field being toggled
            if (field === 'subscribedEmailNotification') {
                toggleData.subscribedEmailNotification = newValue;
            } else if (field === 'subscribedPushNotification') {
                toggleData.subscribedPushNotification = newValue;
            }
            
            const response = await notificationSettingsAPI.toggleSetting(toggleData);
            
            if (!response.data.success) {
                // Revert on failure
                setSettings(prev => prev.map(type => ({
                    ...type,
                    notificationsTypeList: type.notificationsTypeList.map(n => 
                        n.notificationID === notificationId 
                            ? { ...n, [field]: !newValue }
                            : n
                    )
                })));
                console.error('Failed to toggle notification setting');
            }
        } catch (err) {
            // Revert on error
            setSettings(prev => prev.map(type => ({
                ...type,
                notificationsTypeList: type.notificationsTypeList.map(n => 
                    n.notificationID === notificationId 
                        ? { ...n, [field]: !newValue }
                        : n
                )
            })));
            console.error('Error toggling notification setting:', err);
        } finally {
            setUpdating(prev => ({ ...prev, [updateKey]: false }));
        }
    };

    if (loading) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    {eventNameId && <EventNavigation eventNameId={eventNameId} />}
                    <div className={`notification-settings-container ${eventNameId ? 'with-sidebar' : ''}`}>
                        <LoadingSpinner message="Loading notification settings..." />
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    {eventNameId && <EventNavigation eventNameId={eventNameId} />}
                    <div className={`notification-settings-container ${eventNameId ? 'with-sidebar' : ''}`}>
                        <div className="notification-settings-error">
                            <h2>Error</h2>
                            <p>{error}</p>
                            <button onClick={() => navigate(-1)} className="btn-back">
                                Go Back
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <NavigationBar />
            <div className="event-layout">
                {eventNameId && <EventNavigation eventNameId={eventNameId} />}
                <div className={`notification-settings-container ${eventNameId ? 'with-sidebar' : ''}`}>
                    {/* Page Header - Full Width */}
                    <div className="notification-settings-page-header">
                        <button 
                            className="back-button"
                            onClick={() => navigate(`/user/${user?.contact_id}`)}
                        >
                            <ArrowLeft size={20} />
                            Back to Profile
                        </button>
                        <h1>Notification preferences</h1>
                        <p>Manage how you receive notifications</p>
                    </div>

                    <div className="notification-settings-page">
                        <div className="notification-settings-content">
                            {settings.map((type) => (
                                <div key={type.notificationTypeID} className="notification-type-section">
                                    <h2 className="notification-type-title">{type.notificationTypeName}</h2>
                                    <div className="notification-type-card">
                                        {type.notificationsTypeList.map((notification, index) => (
                                            <div 
                                                key={notification.notificationID} 
                                                className={`notification-row ${index < type.notificationsTypeList.length - 1 ? 'with-border' : ''}`}
                                            >
                                                <span className="notification-name">
                                                    {notification.notificationName}
                                                </span>
                                                <div className="notification-toggles">
                                                    {notification.allowEmailNotification && (
                                                        <div className="toggle-group">
                                                            <label className="toggle-switch">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={notification.subscribedEmailNotification}
                                                                    onChange={() => handleToggle(notification, 'subscribedEmailNotification')}
                                                                    disabled={updating[`${notification.notificationID}-subscribedEmailNotification`]}
                                                                />
                                                                <span className="toggle-slider"></span>
                                                            </label>
                                                            <span className="toggle-label">Email</span>
                                                        </div>
                                                    )}
                                                    {notification.allowPushNotification && (
                                                        <div className="toggle-group">
                                                            <label className="toggle-switch">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={notification.subscribedPushNotification}
                                                                    onChange={() => handleToggle(notification, 'subscribedPushNotification')}
                                                                    disabled={updating[`${notification.notificationID}-subscribedPushNotification`]}
                                                                />
                                                                <span className="toggle-slider"></span>
                                                            </label>
                                                            <span className="toggle-label">App</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            {settings.length === 0 && (
                                <div className="no-settings">
                                    <p>No notification settings available.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default NotificationSettings;

